import {
  TwitterCrisisMonitorAttacksCountByBrandsDTO,
  TwitterCrisisMonitorAttacksCountByBrandsItemDTO,
} from '@/data/dto/twitter-crisis-monitor/attacks-count-by-brands.dto';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
  PaginatedRequestParamsDTO,
  ResponseMetaTypeDTO,
} from '@/data/dto/common.dto';
import store from '@/store';
import MOMABffService from '@/services/moma-bff.service';
import { AttacksCountByBrandsStoreContract } from '@/data/store/attacks-count-by-brands.store.contract';
import { TwitterCrisisMonitorAttackDateFiltersDTO } from '@/data/dto/twitter-crisis-monitor/date-filters.dto';
import { FilterStoreContract } from '@/data/store/filter.store.contract';

type BasicState<T> = {
  loading: boolean;
  data: T | null;
  meta?: ResponseMetaTypeDTO;
  error: string | null;
  pagination?: PaginatedRequestParamsDTO;
};

type LocalState = BasicState<TwitterCrisisMonitorAttacksCountByBrandsDTO>;

@Module({
  dynamic: true,
  namespaced: true,
  name: 'AttacksCountByBrandsStore',
  store,
})
export class AttacksCountByBrandsStore
  extends VuexModule
  implements AttacksCountByBrandsStoreContract, FilterStoreContract
{
  bffService = new MOMABffService();

  paginationLimit = 10;

  _state: LocalState = {
    loading: false,
    error: null,
    data: null,
    pagination: {
      offset: 0,
      limit: 10,
    },
  };

  get isLoading(): boolean {
    return this._state.loading;
  }

  get all(): TwitterCrisisMonitorAttacksCountByBrandsItemDTO[] {
    if (!this._state.data) {
      return [];
    }

    return this._state.data.attacksCountByBrand;
  }

  get allForSelect() {
    return this.all.map((item) => item.brand);
  }

  get maxAttacksCount(): number {
    return this.all[0].attacksCount;
  }

  get paginated(): TwitterCrisisMonitorAttacksCountByBrandsItemDTO[] {
    const start = this._state.pagination?.offset || 0;
    const end = start + (this._state.pagination?.limit || this.paginationLimit);

    return this.all.slice(start, end);
  }

  get totalCount(): number {
    return this._state.meta?.totalCount || 0;
  }

  @Mutation
  updateAttacksCountByBrandsState(payload: Partial<LocalState>): void {
    this._state = {
      ...this._state,
      ...payload,
    };
  }

  @Action
  async fetch(payload: {
    pagination?: PaginatedRequestParamsDTO;
    filters?: TwitterCrisisMonitorAttackDateFiltersDTO;
  }): Promise<void> {
    const { pagination } = payload;

    const validatedPagination = pagination || {
      limit: this.paginationLimit,
      offset: 0,
    };

    if (this._state.data) {
      this.updateAttacksCountByBrandsState({
        pagination: validatedPagination,
      });
      return;
    }

    await this._getAttacksCountByBrands();
  }

  @Action
  async _getAttacksCountByBrands(): Promise<void> {
    this.updateAttacksCountByBrandsState({
      loading: true,
      error: null,
      meta: undefined,
      pagination: {
        offset: 0,
        limit: 10,
      },
    });

    try {
      const response =
        await this.bffService.getTwitterCrisisMonitorRealtimeAttacksCountByBrands();
      this.updateAttacksCountByBrandsState({
        data: response.data,
        meta: response.meta,
      });
    } catch (error: any) {
      this.updateAttacksCountByBrandsState({
        error: error?.message,
      });
    } finally {
      this.updateAttacksCountByBrandsState({
        loading: false,
      });
    }
  }
}
